/** What the person can do with an entry that is waiting: remove it, explain it, keep it as a note, or take a copy out of the app. */

import { enqueue, listQueue, newOperationId, removeItem, type QueueItem } from "./queue";
import { refreshQueue, runSync } from "./queue-state";
import type { HoursConfirmation } from "../logbook/HoursInput";

export async function discard(operationId: string): Promise<void> {
  await removeItem(operationId);
  await refreshQueue();
}

/** An engine reading or a bunkering that jumped: send it again with the explanation (a new operation, since the content changed). */
export async function resendWithExplanation(item: QueueItem, confirm: HoursConfirmation): Promise<void> {
  const body = item.kind === "bunkering" ? { ...item.body, hoursConfirm: confirm } : { ...item.body, confirm };
  await enqueue({ ...item, operationId: newOperationId(), body, createdAt: item.createdAt });
  await removeItem(item.operationId);
  await refreshQueue();
  void runSync();
}

const resultWord: Record<string, string> = { done: "utförd", not_done: "ej utförd", fault: "brist" };

/** A checklist that was filled in under another version of the checklist: kept as a note with what was ticked, not forced into the new version. */
export async function checklistAsNote(item: QueueItem): Promise<void> {
  const items = (item.body.items as { result: string; note?: string }[] | undefined) ?? [];
  const done = items.filter((i) => i.result === "done").length;
  const remarks = items.map((i, n) => (i.result !== "done" ? `${n + 1}: ${resultWord[i.result] ?? i.result}${i.note ? ` (${i.note})` : ""}` : "")).filter(Boolean);
  const text = `Checklista "${item.label}" ifylld utan uppkoppling enligt version ${String(item.body.clientRevision ?? "?")}, som har ändrats sedan dess: ${done} av ${items.length} utförda${remarks.length ? `. Anmärkningar: ${remarks.join("; ")}` : ""}.`;
  await enqueue({
    operationId: newOperationId(),
    userId: item.userId,
    kind: "note",
    label: text.slice(0, 60),
    endpoint: "/logbook/journal",
    body: { vesselId: item.body.vesselId, ...(item.body.voyageId ? { voyageId: item.body.voyageId } : {}), type: "note", text, occurredAt: item.body.occurredAt },
    groupKey: "journal",
    createdAt: item.createdAt,
  });
  await removeItem(item.operationId);
  await refreshQueue();
  void runSync();
}

/** A copy of the entries as a file (so nothing is lost if the device cannot send them). */
export async function exportQueue(userId?: string): Promise<string> {
  const items = await listQueue(userId);
  const blob = new Blob([JSON.stringify({ exportedAt: new Date().toISOString(), items }, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `seaslot-osynkade-poster-${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
  return url;
}
